import { atom } from "jotai"

import {
	CatalogManager,
	PhotoMetadataRecord,
	PhotoProcessingStatus,
	PhotoRecord,
	Rating,
} from "@/lib/catalog"
import { getFileId } from "@/utils/file"
import { focusAtom } from "jotai-optics"
import { store } from "@/store/store"
import { PhotoId, stateAtom } from "@/store/undoable-state"
import { ProjectItem } from "@/store/project/projects-service"
import { loadingStateAtom, shouldShowMarkReferenceReminderAtom } from "../app-state"
import {
	cameraFiltersAtom,
	colorFiltersAtom,
	displayGroupAtom,
	fileFormatFiltersAtom,
	flagFiltersAtom,
	isAIEditingModeAtom,
	isCalibrationAtom,
	pathFiltersAtom,
	projectAtom,
	projectPathsAtom,
	ratingFiltersAtom,
	visibilityFiltersAtom,
} from "@/store/project/projects-state"
import { getCalibrationImages2D } from "../processing/calibration-images"
import { ColorTag } from "@/utils/exif"
import { photosCustomOrderAtom } from "./photos-custom-order"
import { adjustmentsHistoryAtom } from "../store-history"
import {
	CullingInfoRecord,
	DisplayGroup,
	HistoryRecordAction,
	PhotoFlagStatus,
	Visibility,
} from "@/lib/catalog/types"
import { createBasicPhotoMetadata } from "@/lib/catalog/utils"
import { getFileForPhoto } from "../files-state"
import { Clusters, generateClustersFromImageInfoAtom } from "@/lib/ai/ai-cluster"
import { getImageInfo, imageInfosAtom } from "../processing/image-info"
import { isAIDebugEnabled } from "@/utils/measurement/feature-flag"
import { sortOptionAtom } from "./photos-view-state"
import { loadCurrentFile } from "../editor/photo-loading"
import { lastSelectionFromClickAtom } from "./photos-selection-state"
import { sortPhotosByOption } from "./photos-sort"
import { getCullingTag } from "../importing/culling-state"

export const currentClustersAtom = atom<Clusters | null>((get) => {
	if (!isAIDebugEnabled() || !get(isAIEditingModeAtom)) {
		return null
	}

	const photos = get(photosAtom)
	const imageInfos = get(imageInfosAtom)
	return generateClustersFromImageInfoAtom(photos, imageInfos)
})

export const currentAffectedPhotosAtom = atom<string[]>((get) => {
	if (!isAIDebugEnabled() || !get(isAIEditingModeAtom)) {
		return []
	}

	const currentPhoto = get(currentPhotoAtom)
	const imageInfos = get(imageInfosAtom)

	if (currentPhoto?.kind !== "reference") {
		return []
	}

	const affectedPhotos = Object.values(imageInfos).filter((imageInfo) =>
		imageInfo.nearestReferences?.some((ref) => ref[0] === currentPhoto.id),
	)
	return affectedPhotos.map((imageInfo) => imageInfo.imageId)
})

export const photosAtom = focusAtom(stateAtom, (optic) => optic.prop("photos"))

export const currentPhotoInitialHistoryIndexAtom = atom(0)

export const currentPhotoIdAtom = focusAtom(stateAtom, (optic) => optic.prop("currentPhotoId"))

export const currentPhotoZoomAtom = atom<"fit" | number>("fit")

export const searchInputAtom = atom<string>("")

export const searchResultPhotoIdsAtom = atom<Set<PhotoId>>(new Set<PhotoId>())

export const currentPhotoAtom = atom(
	(get) => {
		const id = get(currentPhotoIdAtom)
		const photos = get(photosAtom)

		if (id === null) {
			return null
		}

		return photos[id] ?? null
	},
	(get, set, newPhoto: PhotoRecord | null) => {
		const current = get(currentPhotoAtom)
		const initialHistoryIndex = get(currentPhotoInitialHistoryIndexAtom)
		const history = get(adjustmentsHistoryAtom)
		const historyChanged = history.slice(
			initialHistoryIndex,
			current?.historyIndex ? current?.historyIndex + 1 : undefined,
		)

		//If prev photo is modified but not marked as reference during calibration, remind
		if (
			current &&
			current.kind !== "reference" &&
			get(isCalibrationAtom) &&
			historyChanged.length > 0 &&
			historyChanged.some(
				(h) =>
					h.action !== HistoryRecordAction.AutoEnhance &&
					h.action !== HistoryRecordAction.Import &&
					h.action !== HistoryRecordAction.Reset &&
					h.action !== HistoryRecordAction.ApplyAIStyle,
			)
		) {
			set(shouldShowMarkReferenceReminderAtom, true)
		}
		set(currentPhotoIdAtom, newPhoto === null ? null : newPhoto.id)
		set(currentPhotoInitialHistoryIndexAtom, newPhoto?.historyIndex ?? 0)
	},
)

export const isCurrentPhotoManuallyEdited = () => {
	const current = store.get(currentPhotoAtom)
	const initialHistoryIndex = store.get(currentPhotoInitialHistoryIndexAtom)
	const history = store.get(adjustmentsHistoryAtom)
	const historyChanged =
		initialHistoryIndex === current?.historyIndex
			? []
			: history.slice(
					initialHistoryIndex + 1,
					current?.historyIndex ? current?.historyIndex + 1 : undefined,
				)

	if (
		historyChanged.length > 0 &&
		historyChanged.some(
			(h) =>
				h.action !== HistoryRecordAction.AutoEnhance &&
				h.action !== HistoryRecordAction.Import &&
				h.action !== HistoryRecordAction.Reset,
		)
	) {
		return true
	} else {
		return false
	}
}

export const selectedPhotosIDsAtom = focusAtom(stateAtom, (optic) => optic.prop("selectedPhotos"))

export const selectedPhotosAtom = atom(
	(get) => {
		const selectedIds = get(selectedPhotosIDsAtom)
		const photos = get(photosAtom)

		const selectedPhotos: PhotoRecord[] = []

		for (const id of selectedIds) {
			const photo = photos[id]

			if (photo) {
				selectedPhotos.push(photo)
			}
		}

		return selectedPhotos
	},
	(_, set, photos: PhotoRecord[]) => {
		const ids = new Set(photos.map((photo) => photo.id))
		set(selectedPhotosIDsAtom, ids)
	},
)

export const selectedPhotosCountAtom = atom<number>((get) => {
	return get(selectedPhotosIDsAtom).size
})

export const selectedExportablePhotosAtom = atom<PhotoRecord[]>((get) => {
	const photos = get(selectedPhotosAtom)
	return photos.filter(
		(photo) =>
			!photo.isHidden &&
			photo.status !== "error" &&
			!photo.isUnsupported &&
			photo.status === "none",
	)
})

export const selectedExportablePhotosCountAtom = atom<number>((get) => {
	return get(selectedExportablePhotosAtom).length
})

export const sortedPhotosAtom = atom<PhotoRecord[]>((get) => {
	const photos = Object.values(get(photosAtom))

	const sorting = get(sortOptionAtom)
	const metadata = get(metadataAtom)
	const customOrder = get(photosCustomOrderAtom)

	return sortPhotosByOption(photos, sorting, metadata, customOrder)
})

export const allPhotosAtom = atom<PhotoRecord[]>((get) => {
	return Object.values(get(photosAtom))
})
export const anchorPhotosAtom = atom<PhotoRecord[]>((get) => {
	const photos = get(sortedPhotosAtom)
	return photos.filter((photo) => photo.isAnchorImage)
})

export const allVisiblePhotosAtom = atom<PhotoRecord[]>((get) => {
	const photos = get(allPhotosAtom)
	return photos.filter((photo) => !photo.isHidden)
})

export const allHiddenPhotosAtom = atom<PhotoRecord[]>((get) => {
	const photos = get(allPhotosAtom)
	return photos.filter((photo) => photo.isHidden)
})

export const exportablePhotosAtom = atom<PhotoRecord[]>((get) => {
	const photos = get(sortedPhotosAtom)

	const result = photos.filter(
		(photo) => !photo.isHidden && photo.status !== "error" && !photo.isUnsupported,
	)
	return result
})

export const processablePhotosAtom = atom<PhotoRecord[]>((get) => {
	const photos = get(allPhotosAtom)
	return photos.filter((photo) => !photo.isUnsupported && !photo.isHidden)
})

export const unsupportedPhotosAtom = atom<PhotoRecord[]>((get) => {
	const photos = get(allPhotosAtom)
	return photos.filter((photo) => photo.isUnsupported)
})

export const processingSelectedPhotosAtom = atom<PhotoRecord[]>((get) => {
	const photos = get(selectedPhotosAtom)
	return photos.filter((photo) => photo.status === "applying" || photo.status === "pending")
})

const isPhotoInGroup = (photo: PhotoRecord, group: DisplayGroup) => {
	const isValid = !photo.isUnsupported

	const cullingTag = getCullingTag(photo)
	switch (group) {
		case "all":
			return isValid
		case "references":
			return photo.kind === "reference" && isValid
		case "locked":
			return photo.kind === "locked" && isValid
		case "processed":
			return (
				(photo?.kind === "locked" || photo?.styleId !== null) &&
				isValid &&
				photo.status !== "applying" &&
				photo.status !== "pending"
			)
		case "processing":
			return (photo.status === "applying" || photo.status === "pending") && isValid
		case "suggested":
			return photo.isAnchorImage && isValid
		case "keepers":
			return cullingTag === "Keeper" && isValid
		case "candidates":
			return cullingTag === "Candidate" && isValid
		case "rejects":
			return cullingTag === "Reject" && isValid
		case "cullingProcessing":
			return photo.smartCullingTag === null && isValid
		case "unsupported":
			return photo.isUnsupported
	}
}

export const displayGroupPhotosCountAtom = atom<Record<DisplayGroup, number>>((get) => {
	const photos = Object.values(get(photosAtom))

	const counts = {
		all: 0,
		references: 0,
		locked: 0,
		processed: 0,
		processing: 0,
		suggested: 0,
		unsupported: 0,
		keepers: 0,
		candidates: 0,
		rejects: 0,
		cullingProcessing: 0,
	}

	photos.forEach((photo) => {
		if (isPhotoInGroup(photo, "references")) {
			counts.references = counts.references + 1
		}

		if (isPhotoInGroup(photo, "locked")) {
			counts.locked += 1
		}

		if (isPhotoInGroup(photo, "processing")) {
			counts.processing += 1
		}

		if (isPhotoInGroup(photo, "processed")) {
			counts.processed += 1
		}

		if (isPhotoInGroup(photo, "suggested")) {
			counts.suggested += 1
		}

		if (isPhotoInGroup(photo, "keepers")) {
			counts.keepers += 1
		}

		if (isPhotoInGroup(photo, "candidates")) {
			counts.candidates += 1
		}

		if (isPhotoInGroup(photo, "rejects")) {
			counts.rejects += 1
		}

		if (isPhotoInGroup(photo, "cullingProcessing")) {
			counts.cullingProcessing += 1
		}

		if (isPhotoInGroup(photo, "unsupported")) {
			counts.unsupported += 1
		}

		if (isPhotoInGroup(photo, "all")) {
			counts.all = counts.all + 1
		}
	})

	return counts
})

export const starRatingPhotosCountAtom = atom<Record<Rating, number>>((get) => {
	const photos = Object.values(get(photosAtom))

	let noRating = 0
	let oneStar = 0
	let twoStar = 0
	let threeStar = 0
	let fourStar = 0
	let fiveStar = 0

	photos.forEach((photo) => {
		switch (photo.rating) {
			case null:
				noRating += 1
				break
			case 0:
				noRating += 1
				break
			case 1:
				oneStar += 1
				break
			case 2:
				twoStar += 1
				break
			case 3:
				threeStar += 1
				break
			case 4:
				fourStar += 1
				break
			case 5:
				fiveStar += 1
				break
		}
	})
	return { 0: noRating, 1: oneStar, 2: twoStar, 3: threeStar, 4: fourStar, 5: fiveStar }
})

export const colorPhotosCountAtom = atom<Record<ColorTag, number>>((get) => {
	const photos = Object.values(get(photosAtom))

	let noColor = 0
	let red = 0
	let green = 0
	let yellow = 0
	let blue = 0
	let purple = 0

	photos.forEach((photo) => {
		switch (photo.colorTag) {
			case null:
			case "No Color":
				noColor += 1
				break
			case "Red":
				red += 1
				break
			case "Green":
				green += 1
				break
			case "Yellow":
				yellow += 1
				break
			case "Blue":
				blue += 1
				break
			case "Purple":
				purple += 1
				break
		}
	})

	return {
		"No Color": noColor,
		Red: red,
		Green: green,
		Yellow: yellow,
		Blue: blue,
		Purple: purple,
	}
})

export const flagPhotosCountAtom = atom<Record<PhotoFlagStatus, number>>((get) => {
	const photos = Object.values(get(photosAtom))

	let unflagged = 0
	let flagged = 0
	let rejected = 0

	photos.forEach((photo) => {
		switch (photo.flag) {
			case null:
			case "unflagged":
				unflagged += 1
				break
			case "flagged":
				flagged += 1
				break
			case "rejected":
				rejected += 1
				break
		}
	})
	return {
		unflagged,
		flagged,
		rejected,
	}
})

export const visibilityPhotosCountAtom = atom<Record<Visibility, number>>((get) => {
	const photos = Object.values(get(photosAtom))

	let visible = 0
	let hidden = 0

	photos.forEach((photo) => {
		if (photo.isHidden) {
			hidden += 1
		} else {
			visible += 1
		}
	})
	return {
		visible,
		hidden,
	}
})

export const getCamera = (metadata: PhotoMetadataRecord) => {
	if (metadata.cameraModel) {
		return metadata.cameraModel
	} else if (metadata.cameraMake) {
		return metadata.cameraMake
	} else {
		return "Unknown Camera"
	}
}

export const getCameraAndSerial = (metadata: PhotoMetadataRecord) => {
	let result = getCamera(metadata)

	if (metadata.cameraSerialNumber) {
		result = result.concat(` (${metadata.cameraSerialNumber})`)
	}

	return result
}

export const displayedPhotosAtom = atom<PhotoRecord[]>((get) => {
	const sortedPhotos = get(sortedPhotosAtom)

	const metadata = get(metadataAtom)

	const ratingFilters = get(ratingFiltersAtom)
	const displayGroup = get(displayGroupAtom)
	const pathFilters = get(pathFiltersAtom)
	const colorFilters = get(colorFiltersAtom)
	const cameraFilters = get(cameraFiltersAtom)
	const flagFilters = get(flagFiltersAtom)
	const fileFormatFilters = get(fileFormatFiltersAtom)
	const visibilityFilters = get(visibilityFiltersAtom)
	const searchInput = get(searchInputAtom)
	const searchResults = get(searchResultPhotoIdsAtom)

	const matchesFolder = (photo: PhotoRecord) => {
		const path = getPhotoPath(photo)
		return path ? pathFilters.has(path) : false
	}

	const matchesRating = (photo: PhotoRecord) => {
		return ratingFilters.size === 0 ? true : ratingFilters.has(photo.rating ?? 0)
	}

	const matchesColor = (photo: PhotoRecord) => {
		return colorFilters.size === 0 ? true : colorFilters.has(photo.colorTag ?? "No Color")
	}

	const matchesFlag = (photo: PhotoRecord) => {
		return flagFilters.size === 0 ? true : flagFilters.has(photo.flag ?? "unflagged")
	}

	const matchesVisibility = (photo: PhotoRecord) => {
		return visibilityFilters.size === 0
			? false
			: visibilityFilters.has(photo.isHidden ? "hidden" : "visible")
	}

	const matchesCamera = (photo: PhotoRecord) => {
		if (cameraFilters.size === 0) return true

		const photoMetadata = metadata[photo.id]
		if (photoMetadata) {
			return !cameraFilters.has(getCameraAndSerial(photoMetadata))
		}
		return true
	}

	const matchesFileFormat = (photo: PhotoRecord) => {
		if (fileFormatFilters.size === 0) return true

		const photoMetadata = metadata[photo.id]
		if (photoMetadata) {
			let fileFormat = photoMetadata.extension.toUpperCase()
			//Merging JPG and JPEG
			if (fileFormat === "JPG") {
				fileFormat = "JPEG"
			}
			return !fileFormatFilters.has(fileFormat)
		}
		return true
	}

	const matchesSearch = (photo: PhotoRecord) => {
		if (!searchInput.trim()) return true
		return searchResults.has(photo.id)
	}

	const filtered = sortedPhotos.filter((photo) => {
		return (
			isPhotoInGroup(photo, displayGroup) &&
			matchesSearch(photo) &&
			matchesVisibility(photo) &&
			matchesFolder(photo) &&
			matchesRating(photo) &&
			matchesColor(photo) &&
			matchesCamera(photo) &&
			matchesFileFormat(photo) &&
			matchesFlag(photo)
		)
	})

	return filtered
})
// Cache the previous array of IDs to avoid unnecessary re-renders
let previousDisplayedPhotoIds: string[] | null = null

export const displayedPhotoIdsAtom = atom((get) => {
	const displayedPhotos = get(displayedPhotosAtom)
	const newIds = displayedPhotos.map((photo) => photo.id)

	if (
		previousDisplayedPhotoIds &&
		previousDisplayedPhotoIds.length === newIds.length &&
		previousDisplayedPhotoIds.every((id, i) => id === newIds[i])
	) {
		return previousDisplayedPhotoIds
	}

	previousDisplayedPhotoIds = newIds
	return newIds
})

/* Path */

export const filePathsAtom = atom<Record<PhotoId, string>>({})

export const getPhotoPath = (photo: PhotoRecord) => {
	const paths = store.get(filePathsAtom)
	if (photo.parentId) {
		return paths[photo.parentId]
	} else {
		return paths[photo.id]
	}
}

export const currentPathsCountAtom = atom((get) => {
	const pathFilters = get(pathFiltersAtom)
	const project = get(projectAtom)
	if (!project) {
		return 0
	}
	const projectPaths = get(projectPathsAtom)

	if (pathFilters.size === projectPaths.length) {
		return project.photoCount
	}

	let count = 0
	for (const path of pathFilters) {
		const pathInfo = projectPaths.find((p) => p.path === path)
		count += pathInfo?.photoCount ?? 0
	}
	return count
})

/* Metadata */

export const metadataAtom = atom<Record<PhotoId, PhotoMetadataRecord>>({})

export const currentMetadataAtom = atom((get) => {
	const metadata = get(metadataAtom)
	const currentPhoto = get(currentPhotoAtom)

	return currentPhoto ? metadata[currentPhoto.id] : null
})

export const allCamerasAtom = atom((get) => {
	const allMetadata = get(metadataAtom)
	const camerasInfo: Record<string, number> = {}
	for (const metadata of Object.values(allMetadata)) {
		const camera = getCameraAndSerial(metadata)
		if (camerasInfo[camera]) {
			camerasInfo[camera] += 1
		} else {
			camerasInfo[camera] = 1
		}
	}
	return camerasInfo
})

export const allFileFormatsAtom = atom((get) => {
	const allMetadata = get(metadataAtom)
	const fileFormatInfo: Record<string, number> = {}
	for (const metadata of Object.values(allMetadata)) {
		let fileFormat = metadata.extension.toUpperCase()
		//Merging JPG and JPEG
		if (fileFormat === "JPG") {
			fileFormat = "JPEG"
		}
		if (fileFormatInfo[fileFormat]) {
			fileFormatInfo[fileFormat] = (fileFormatInfo[fileFormat] as number) + 1
		} else {
			fileFormatInfo[fileFormat] = 1
		}
	}
	return fileFormatInfo
})

export const getPhotoMetadata = (id: PhotoId) => {
	return store.get(metadataAtom)[id]
}

export const updatePhotoMetadata = (
	fileOrId: File | PhotoId,
	updatedMetadata: PhotoMetadataRecord,
) => {
	const id = fileOrId instanceof File ? getFileId(fileOrId) : fileOrId
	const metadata = store.get(metadataAtom)

	store.set(metadataAtom, {
		...metadata,
		[id]: updatedMetadata,
	})

	CatalogManager.storePhotoMetadataRecord(updatedMetadata)
}

export const updatePhotosMetadata = async (
	handler: (updates: Record<PhotoId, Partial<PhotoMetadataRecord>>) => void,
	createIfMissing: boolean = false,
) => {
	const metadata = store.get(metadataAtom)

	const changes: Record<PhotoId, Partial<PhotoMetadataRecord>> = {}
	const stagedUpdates: Record<PhotoId, PhotoMetadataRecord> = {}

	handler(changes)

	for (const id in changes) {
		let photoMetadata = metadata[id]

		if (!photoMetadata) {
			if (!createIfMissing) {
				continue
			} else {
				const file = getFileForPhoto(id)

				if (!file) {
					continue
				}

				photoMetadata = createBasicPhotoMetadata(file, id)
			}
		}

		const updatedMetadata: PhotoMetadataRecord = {
			...photoMetadata,
			...changes[id],
		}

		stagedUpdates[id] = updatedMetadata
	}

	store.set(metadataAtom, (prev) => {
		return {
			...prev,
			...stagedUpdates,
		}
	})

	await CatalogManager.bulkUpdatePhotoMetadataRecords(stagedUpdates)
}

export const deletePhotoMetadata = async (fileOrId: File | PhotoId) => {
	const id = fileOrId instanceof File ? getFileId(fileOrId) : fileOrId
	const metadata = { ...store.get(metadataAtom) }

	delete metadata[id]

	store.set(metadataAtom, metadata)

	await CatalogManager.deletePhotoMetadataRecord(id)
}

// Culling info
export const cullingInfoAtom = atom<Record<PhotoId, CullingInfoRecord>>({})

export const getCullingInfosCount = () => {
	const result = store.get(cullingInfoAtom)
	return Object.keys(result).length
}

export const getCullingInfo = (id: PhotoId) => {
	return store.get(cullingInfoAtom)[id]
}

export const updateCullingInfos = (
	handler: (updates: Record<PhotoId, Partial<CullingInfoRecord>>) => void,
	createIfMissing: boolean = false,
) => {
	const info = store.get(cullingInfoAtom)

	const changes: Record<PhotoId, CullingInfoRecord> = {}
	const stagedUpdates: Record<PhotoId, CullingInfoRecord> = {}

	handler(changes)

	for (const id in changes) {
		let singleInfo = info[id]

		if (!singleInfo) {
			if (!createIfMissing) {
				continue
			} else {
				singleInfo = { photoId: id, faceInfo: [] }
			}
		}

		const baseInfo = stagedUpdates[id] ?? { ...singleInfo }
		const updatedInfo: CullingInfoRecord = {
			...baseInfo,
			...changes[id],
		}

		stagedUpdates[id] = updatedInfo
	}

	store.set(cullingInfoAtom, {
		...info,
		...stagedUpdates,
	})

	CatalogManager.bulkUpdateCullingInfoRecords(changes)
}

export const updateCullingInfo = async (id: PhotoId, updatedInfo: CullingInfoRecord) => {
	const info = store.get(cullingInfoAtom)

	store.set(cullingInfoAtom, {
		...info,
		[id]: updatedInfo,
	})

	return CatalogManager.storeCullingInfoRecord(updatedInfo)
}

export const getPhotoStatus = (id: PhotoId): PhotoProcessingStatus => {
	return store.get(photosAtom)[id]?.status || "none"
}

export const autoSelectNextPhotoWithRemoval = (
	removed: PhotoId[],
	previousPhotos: PhotoRecord[],
) => {
	const currentPhotoId = store.get(currentPhotoIdAtom)

	//If current photo is removed (deleted or hidden), select the next available photo
	if (currentPhotoId && removed.includes(currentPhotoId)) {
		let index = previousPhotos.findIndex((photo) => photo.id === currentPhotoId)
		let nextPhoto: PhotoRecord | null = null
		while (nextPhoto === null && index < previousPhotos.length) {
			const photo = previousPhotos[index]
			if (photo && !removed.includes(photo.id)) {
				nextPhoto = photo
			} else {
				index += 1
			}
		}

		if (nextPhoto) {
			store.set(currentPhotoAtom, nextPhoto)
			store.set(selectedPhotosAtom, [nextPhoto])
		} else {
			const remainingPhotos = previousPhotos.filter((photo) => !removed.includes(photo.id))
			const newPhoto = remainingPhotos.at(-1) ?? null
			store.set(currentPhotoAtom, newPhoto)
			store.set(selectedPhotosAtom, newPhoto ? [newPhoto] : [])
		}
		void loadCurrentFile()
	}
}

export const autoSelectNextPhoto = (photosToSearch: string[]) => {
	const currentPhotoId = store.get(currentPhotoIdAtom)
	const allSortedPhotos = store.get(sortedPhotosAtom)

	const firstElement = photosToSearch[0]
	if (!firstElement) {
		return
	}

	if (!currentPhotoId || photosToSearch.findIndex((id) => id === currentPhotoId) !== -1) {
		return
	}

	const index = allSortedPhotos.findIndex((photo) => photo.id === currentPhotoId)
	const nextPhoto = allSortedPhotos.slice(index).find((photo) => {
		if (photosToSearch.findIndex((id) => id === photo.id) !== -1) {
			return photo
		}
	})

	if (nextPhoto) {
		store.set(currentPhotoAtom, nextPhoto)
		store.set(selectedPhotosAtom, [nextPhoto])
		store.set(lastSelectionFromClickAtom, false)

		void loadCurrentFile()
		return
	}

	const lastPhotoId = photosToSearch.at(-1)
	if (lastPhotoId) {
		store.set(currentPhotoIdAtom, lastPhotoId)
		store.set(selectedPhotosIDsAtom, new Set([lastPhotoId]))
		store.set(lastSelectionFromClickAtom, false)

		void loadCurrentFile()
	}
}

export const updatePhoto = (
	id: PhotoId,
	changes: Partial<PhotoRecord>,
	shouldStorePhoto: boolean = true,
) => {
	const photos = store.get(photosAtom)
	const photo = photos[id]

	if (!photo) {
		if (store.get(projectAtom)) {
			throw new Error("Could not update photo. Photo does not exist")
		} else {
			//No project is open, so we can't update the photo any more, this is expected
			return
		}
	}

	const updatedPhoto: PhotoRecord = {
		...photo,
		...changes,
	}

	if (shouldStorePhoto) {
		//Update atom instantly
		store.set(photosAtom, {
			...photos,
			[id]: updatedPhoto,
		})

		//Update catalog asynchronously
		void CatalogManager.updatePhotoRecord(id, changes)
	}

	return updatedPhoto
}

export const updatePhotos = async (
	handler: (photos: Record<PhotoId, Partial<PhotoRecord>>) => void,
) => {
	const photos = store.get(photosAtom)

	const changes: Record<PhotoId, Partial<PhotoRecord>> = {}
	const stagedPhotoUpdates: Record<PhotoId, PhotoRecord> = {}

	handler(changes)

	for (const id in changes) {
		const photo = photos[id]

		if (!photo) {
			continue
		}

		const basePhoto = stagedPhotoUpdates[id] ?? { ...photo }
		const updatedPhoto: PhotoRecord = {
			...basePhoto,
			...changes[id],
		}

		stagedPhotoUpdates[id] = updatedPhoto
	}

	store.set(photosAtom, (prev) => {
		return {
			...prev,
			...stagedPhotoUpdates,
		}
	})

	if (Object.keys(changes).length > 0) {
		await CatalogManager.bulkUpdatePhotoRecords(changes)
	}
}

export const isExpressSetupAtom = atom<boolean>((get) => {
	const project = get(projectAtom)
	if (!project) {
		return false
	} else {
		return get(isCalibrationAtom) && (project.isExpressSetup ?? false)
	}
})

export const updateCalibrationPhotos = (project: ProjectItem, isExpressSetup: boolean) => {
	const existingAnchorPhotos = store.get(anchorPhotosAtom)
	if (isExpressSetup) {
		void updatePhotos((photos) => {
			for (const photo of existingAnchorPhotos) {
				photos[photo.id] = { isAnchorImage: false }
			}
		})
		return
	}

	const photos = store.get(allVisiblePhotosAtom)
	const metadata = store.get(metadataAtom)

	//if no anchor images or if number of photos has changed
	const needsRegenerate =
		project.photoCount != Object.keys(photos).length || existingAnchorPhotos.length === 0
	if (needsRegenerate) {
		store.set(loadingStateAtom, { status: "preparingCalibration" })
		const result = getCalibrationImages2D(Object.values(photos), metadata)

		const newAnchorPhotos = result.finalPhotos

		void updatePhotos((photos) => {
			for (const photo of existingAnchorPhotos) {
				photos[photo.id] = { isAnchorImage: false }
			}

			for (const photo of newAnchorPhotos) {
				photos[photo.id] = { isAnchorImage: true }
			}
		})
	}
}

export const BorderColorValues = ["red", "yellow", "orange", "green"] as const
export type BorderColor = (typeof BorderColorValues)[number]
export const aiDebugBorderAtom = atom<Record<PhotoId, BorderColor>>({})

export function updatePhotoBorderColorForAIDebug() {
	const isAIEditingMode = store.get(isAIEditingModeAtom)
	if (!isAIEditingMode || !isAIDebugEnabled()) {
		store.set(aiDebugBorderAtom, {})
		return
	}

	const photo = store.get(currentPhotoAtom)
	if (!photo) {
		store.set(aiDebugBorderAtom, {})
		return
	}
	const currentClusters = store.get(currentClustersAtom)
	const currentImageInfo = getImageInfo(photo.id)

	const updates: Record<PhotoId, BorderColor> = {}

	if (photo?.kind === "reference") {
		for (const photoId of store.get(currentAffectedPhotosAtom)) {
			updates[photoId] = "green"
		}
		updates[photo.id] = "red"
	} else {
		const refs = currentImageInfo?.nearestReferences ?? []
		for (const ref of refs) {
			updates[ref[0]] = "red"
		}
	}

	const keyNeighborId = currentImageInfo?.keyNeighborId
	const clusterMembers = currentClusters?.[keyNeighborId ?? ""] ?? []
	for (const photoId of clusterMembers) {
		updates[photoId] = "yellow"
	}

	if (keyNeighborId) {
		updates[keyNeighborId] = "orange"
	}

	store.set(aiDebugBorderAtom, updates)
}

export const getRandomPhotosForStylePreview = (avoiding?: PhotoId[]) => {
	const photos = store.get(allVisiblePhotosAtom)
	const unsupportedPhotos = store.get(unsupportedPhotosAtom)

	// Combine explicit avoiding list with unsupported photos
	const allAvoiding = new Set([...(avoiding ?? []), ...unsupportedPhotos.map((p) => p.id)])
	const photosFiltered =
		allAvoiding.size > 0 ? photos.filter((p) => !allAvoiding.has(p.id)) : photos

	if (!photosFiltered || photosFiltered.length === 0) return []

	if (photosFiltered.length <= 4) {
		return photosFiltered
	}

	// Select evenly-spaced photos for consistent, deterministic preview - deterministic clustering
	photosFiltered.sort((a, b) => a.fileName.localeCompare(b.fileName))
	const step = photosFiltered.length / 4
	const previewPhotos = [
		photosFiltered[Math.floor(step * 0)],
		photosFiltered[Math.floor(step * 1)],
		photosFiltered[Math.floor(step * 2)],
		photosFiltered[Math.floor(step * 3)],
	].filter((p): p is PhotoRecord => p !== undefined)

	return previewPhotos
}
